import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import * as date from '@owl-nest/date'
import { t } from '@owl-nest/localize'

import { Location } from './type'

const DAYS_OF_WEEK = date
  .eachDayOfInterval({
    start: date.startOfWeek(new Date(), { weekStartsOn: 0 }),
    end: date.endOfWeek(new Date(), { weekStartsOn: 0 }),
  })
  .map((dayDate) => date.format(dayDate, 'EEEE'))

type LocationCardProps = {
  bottomRight?: React.ReactNode
  className?: string
  index?: number
  location: Location
  wrapper?: keyof React.ReactHTML | React.ComponentType<{ className?: string; children?: React.ReactNode }>
}

function LocationCardComponent({ location, className, index, bottomRight }: LocationCardProps) {
  const [isTimetableOpen, setIsTimetableOpen] = React.useState(false)

  const timeTable = React.useMemo(() => {
    const timeTable = location.timetable.map((entry) => ({ ...entry, day: Number(entry.day) }))

    timeTable.sort((a, b) => a.day - b.day)
    return timeTable
  }, [location.timetable])

  return (
    <div className={className}>
      <Header>
        <Name>
          {index !== undefined ? (
            <>
              {index} - {location.name}
            </>
          ) : (
            <>{location.name}</>
          )}
        </Name>
        {location.distance && <Distance>{formatDistance(location.distance)}</Distance>}
      </Header>
      <Address>{location.address}</Address>

      <CTA>
        <plume.styles.button.UnstyledButton
          onClickCapture={(event) => {
            setIsTimetableOpen((isOpen) => !isOpen)
            event.stopPropagation()
          }}
          type="button"
        >
          <SeeMore as="span">
            <>
              {t('See opening hours')}{' '}
              {isTimetableOpen ? (
                <plume.glyphs.stroke.CaretUp size={12} />
              ) : (
                <plume.glyphs.stroke.CaretDown size={12} />
              )}
            </>
          </SeeMore>
        </plume.styles.button.UnstyledButton>

        {bottomRight}
      </CTA>

      {isTimetableOpen && (
        <TimeTable>
          {timeTable.map((time) => {
            return (
              <>
                <plume.styles.copy.S.regular>{DAYS_OF_WEEK[time.day]}</plume.styles.copy.S.regular>
                <plume.styles.copy.S.regular>{formatOpeningHours(time.hours)}</plume.styles.copy.S.regular>
              </>
            )
          })}
        </TimeTable>
      )}
    </div>
  )
}

function formatDistance(distance: number) {
  if (distance < 1) {
    return `${Math.floor(distance * 100) * 10} m`
  }
  return `${Math.floor(distance)} km`
}

function formatOpeningHours(hours: { open: string; close: string }[] = []) {
  if (hours.length === 0) {
    return 'Fermé'
  }
  return hours.map(({ open, close }) => `${open}-${close}`).join(' ')
}

const CTA = styled.div`
  display: flex;
  justify-content: space-between;

  ${plume.LinkAsButton} {
    margin: 0;
    padding: 0;
  }
`

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const TimeTable = styled.div`
  display: grid;
  grid-template-columns: min-content auto;
  gap: 4px 20px;
`

const Name = styled(plume.styles.copy.M.semiBold)``

const Address = styled(plume.styles.copy.S.regular)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Distance = styled(plume.styles.copy.XS.regular)`
  color: ${plume.COLORS.TEXT_TERTIARY};
`

const SeeMore = styled(plume.styles.copy.S.semiBold)`
  margin-bottom: 6px;

  ${plume.glyphs.Icon} {
    color: ${plume.COLORS.PRIMARY_SAND_500};
    margin-left: 5px;
  }
`

export const LocationCard = styled(LocationCardComponent)`
  background: ${plume.COLORS.PRIMARY_GREY_000};
  padding: 8px 16px;

  ${Address} {
    white-space: pre;
  }

  ${plume.styles.button.UnstyledButton} {
    z-index: 10;
    position: relative;
  }
`
